import type { PushAudience, PushAudienceMembership } from '@/types/push-campaign';
import { PUSH_AUDIENCE_DAYS_MAX, PUSH_AUDIENCE_DAYS_MIN, PUSH_EXCLUDE_MAX } from '@/types/push-campaign';
import { SELECT_OPTIONS } from '@/constants/select-options';

/**
 * Client-side view of a push audience. The database owns the definition
 * (validate_push_audience / push_audience_member_ids); these helpers only
 * shape what the composer sends and describe it back in words. Keep the rules
 * here a strict mirror of the SQL, never wider — a filter the client accepts
 * and the DB rejects is a confusing error, the reverse is a wrong send.
 */

export const PUSH_MEMBERSHIP_OPTIONS: { value: PushAudienceMembership; label: string; hint: string }[] = [
  { value: 'any', label: 'Any', hint: 'No membership condition' },
  { value: 'active', label: 'Active membership', hint: 'A valid membership covering today (Bali date)' },
  { value: 'expired', label: 'Expired membership', hint: 'Had a membership, none covers today' },
  { value: 'none', label: 'Never had a membership', hint: 'No membership row at all' },
];

const MEMBERSHIP_LABELS: Record<PushAudienceMembership, string> = {
  any: 'Any membership status',
  active: 'Active membership',
  expired: 'Expired membership',
  none: 'Never had a membership',
};

const MEMBER_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  SELECT_OPTIONS.memberType.map((o) => [o.value, o.label]),
);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanList(values: string[] | undefined): string[] {
  if (!values) return [];
  const out: string[] = [];
  for (const raw of values) {
    const v = raw.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

function cleanDays(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The audience as it is sent: defaults dropped, lists de-duplicated, blank
 * numbers removed, exclusions sorted (so two audiences that differ only in
 * tick order are the same audience). `{}` is the widest possible segment —
 * every live member with the app — so a caller must never treat "empty" as
 * "nobody".
 */
export function normalizePushAudience(input: PushAudience): PushAudience {
  const out: PushAudience = {};
  if (input.membership && input.membership !== 'any') out.membership = input.membership;
  const plans = cleanList(input.plans);
  if (plans.length) out.plans = plans;
  const memberTypes = cleanList(input.member_types);
  if (memberTypes.length) out.member_types = memberTypes;
  const checkedIn = cleanDays(input.checked_in_within_days);
  if (checkedIn !== undefined) out.checked_in_within_days = checkedIn;
  const notCheckedIn = cleanDays(input.not_checked_in_within_days);
  if (notCheckedIn !== undefined) out.not_checked_in_within_days = notCheckedIn;
  const joined = cleanDays(input.joined_within_days);
  if (joined !== undefined) out.joined_within_days = joined;
  const excluded = cleanList(input.exclude_member_ids).map((id) => id.toLowerCase()).sort();
  if (excluded.length) out.exclude_member_ids = excluded;
  return out;
}

/** The same audience with the per-member exclusions removed — what the composer's list is fetched with. */
export function withoutExclusions(audience: PushAudience): PushAudience {
  const rest: PushAudience = { ...audience };
  delete rest.exclude_member_ids;
  return rest;
}

/**
 * First rule the (normalized) audience breaks, as the sentence the DB would
 * raise, or null when it is sendable. Mirrors validate_push_audience().
 */
export function validatePushAudience(audience: PushAudience): string | null {
  if (audience.membership === 'none' && (audience.plans?.length ?? 0) > 0) {
    return 'Plans cannot be combined with "Never had a membership"';
  }
  if (audience.membership === 'none' && (audience.member_types?.length ?? 0) > 0) {
    return 'Member types cannot be combined with "Never had a membership"';
  }
  if (audience.checked_in_within_days !== undefined && audience.not_checked_in_within_days !== undefined) {
    return 'Choose either "checked in within" or "not checked in within", not both';
  }
  for (const [key, label] of [
    ['checked_in_within_days', 'Checked in within'],
    ['not_checked_in_within_days', 'Not checked in within'],
    ['joined_within_days', 'Joined within'],
  ] as const) {
    const v = audience[key];
    if (v === undefined) continue;
    if (!Number.isInteger(v) || v < PUSH_AUDIENCE_DAYS_MIN || v > PUSH_AUDIENCE_DAYS_MAX) {
      return `${label} must be a whole number of days between ${PUSH_AUDIENCE_DAYS_MIN} and ${PUSH_AUDIENCE_DAYS_MAX}`;
    }
  }
  const excluded = audience.exclude_member_ids ?? [];
  if (excluded.length > PUSH_EXCLUDE_MAX) {
    return `At most ${PUSH_EXCLUDE_MAX.toLocaleString()} members can be unticked`;
  }
  if (excluded.some((id) => !UUID_RE.test(id))) {
    return 'Unticked members must be member ids';
  }
  return null;
}

/** True when the audience carries no filter at all — every member with the app. */
export function isEveryoneAudience(audience: PushAudience): boolean {
  return Object.keys(normalizePushAudience(withoutExclusions(audience))).length === 0;
}

/**
 * Human sentence for an audience, e.g.
 * "Active membership · Yearly, Monthly · Staff (50% F&B) · Checked in within 30 days · 3 unticked".
 * Stored on the campaign at send time (audience_label) so history keeps the
 * words used then, even if this function changes later.
 */
export function describePushAudience(input: PushAudience): string {
  const a = normalizePushAudience(input);
  const parts: string[] = [];
  parts.push(MEMBERSHIP_LABELS[a.membership ?? 'any']);
  if (a.plans?.length) parts.push(a.plans.join(', '));
  if (a.member_types?.length) parts.push(a.member_types.map((t) => MEMBER_TYPE_LABELS[t] ?? t).join(', '));
  if (a.checked_in_within_days !== undefined) parts.push(`Checked in within ${a.checked_in_within_days} days`);
  if (a.not_checked_in_within_days !== undefined) parts.push(`Not checked in within ${a.not_checked_in_within_days} days`);
  if (a.joined_within_days !== undefined) parts.push(`Joined within ${a.joined_within_days} days`);
  const excluded = a.exclude_member_ids?.length ?? 0;
  const base = parts.length === 1 && (a.membership ?? 'any') === 'any' ? 'Everyone with the app' : parts.join(' · ');
  return excluded > 0 ? `${base} · ${excluded} unticked` : base;
}
